// Port of SuiteModel/TextVariants.swift: the Revise room's consistency pass — where the book spells, hyphenates,
// capitalises or punctuates one word two ways. A finding is a set of two forms, raised only when both are in the book,
// or when the style sheet holds a choice and the other form turns up. Positions are Unicode scalars in each text; texts
// are the manuscript's scenes in reading order. Exact: every rule, list and message is the Swift's.
import { linkScalarRanges } from '../proseMarkdown.ts';
import { stableUUID } from '../hashing.ts';
import { graphemes, trimCFWhitespace } from '../unicode.ts';
import { scalarLines } from '../text/quotations.ts';
import { charIsLowercase, charIsUppercase, fromScalars, isAlphabetic, isDigit, isUppercase, toScalars } from '../text/scalars.ts';
import { listMarkers, spanRanges, wordsIn } from './textCleanup.ts';
import type { NumberStyle } from './styleSheet.ts';

export type VariantKind = 'spelling-variants' | 'hyphenation' | 'names' | 'honorifics' | 'family-words' | 'numbers';
export const VARIANT_KINDS: readonly VariantKind[] = ['spelling-variants', 'hyphenation', 'names', 'honorifics', 'family-words', 'numbers'];

export const variantKindName = (k: VariantKind): string => ({
  'spelling-variants': 'Spelling variants', hyphenation: 'Hyphenation', names: 'Names', honorifics: 'Mr or Mr.', 'family-words': 'Mum and mum',
  numbers: 'Numbers',
})[k];

export const variantKindSummary = (k: VariantKind): string => ({
  'spelling-variants': 'A word spelt two ways in the book: grey and gray, colour and color, realise and realize, travelled and traveled.',
  hyphenation: 'A word hyphenated in one place and closed up in another: e-mail and email.',
  names: "A capitalised word a letter short, over or swapped from a character's or place's name (Uterson beside Utterson).",
  honorifics: 'Mr, Mrs, Ms and Dr with a full stop in one place and without in another.',
  'family-words': 'Mum, Dad and the like: a capital when used as a name (“Mum said”), a small letter after my, his or a (“my mum”).',
  numbers: 'Numbers to one hundred written as digits in a book that spells them out (or whose style sheet says to).',
})[k];

/** `Kind.choosesAForm`: one form is chosen for the whole book; family words are fixed place by place, by their rule. */
export const variantChoosesAForm = (k: VariantKind) => k !== 'family-words';

export interface VariantOccurrence {
  text: number;
  range: [number, number];
  written: string;
  form: string;
  /** The words in the other form, case kept; null when only the writer can say. */
  alternative: string | null;
}

export interface VariantSet {
  id: string;
  kind: VariantKind;
  key: string;
  forms: string[];
  counts: Record<string, number>;
  occurrences: VariantOccurrence[];
  kept: string | null;
}

/** `VariantSet.flagged`. */
export function flaggedOccurrences(set: VariantSet): VariantOccurrence[] {
  if (set.kind === 'family-words') return set.occurrences.filter((o) => o.alternative !== null);
  const keep = set.kept ?? set.forms[0];
  return set.occurrences.filter((o) => o.form !== keep);
}

/** A choice from the consistency pass, kept in the style sheet: the form to use, or both (`keep` null). */
export interface VariantChoice {
  kind: VariantKind;
  /** Sorted by their lowercased spelling. */
  forms: string[];
  keep: string | null;
}

const lowerSort = (a: string, b: string) => {
  const x = a.toLowerCase(), y = b.toLowerCase();
  return x < y ? -1 : x > y ? 1 : 0;
};

export function makeChoice(kind: VariantKind, forms: string[], keep: string | null): VariantChoice {
  return { kind, forms: [...forms].sort(lowerSort), keep };
}

export const variantKey = (kind: VariantKind, forms: string[]) => `${kind}:${forms.map((f) => f.toLowerCase()).sort().join('|')}`;
export const choiceKey = (c: VariantChoice) => variantKey(c.kind, c.forms);
export const choiceID = (c: VariantChoice) => stableUUID('style-choice:' + choiceKey(c));
/** The choice as the Swift's JSONEncoder (sorted keys) writes it; `keep` left out when both are kept. */
export const choiceJSON = (c: VariantChoice) =>
  JSON.stringify(c.keep === null ? { forms: c.forms, kind: c.kind } : { forms: c.forms, keep: c.keep, kind: c.kind });

export interface VariantOptions {
  names?: string[];
  choices?: VariantChoice[];
  numbers?: NumberStyle | null;
  language?: string | null;
}

// MARK: Words

export interface Token { range: [number, number]; word: string; parts: { range: [number, number]; word: string }[] }

export interface Scan { s: string[]; tokens: Token[]; lowerWords: Set<string> }

const firstChar = (w: string) => graphemes(w)[0] ?? '';
const isLetterChar = (c: string) => /^\p{L}/u.test(c);
const allLetters = (w: string) => graphemes(w).every(isLetterChar);
const capitalFirst = (w: string) => { const g = graphemes(w); return g.length ? g[0].toUpperCase() + g.slice(1).join('') : w; };

/** `TextVariants.Scan`: the words of a text, link syntax, span classes and fences left out. */
export function scan(s: string[]): Scan {
  const skip = new Set<number>();
  for (const link of linkScalarRanges(s)) for (const [a, b] of link.syntax) for (let k = a; k < b; k++) skip.add(k);
  for (const [a, b] of spanRanges(s)) {
    let close = -1;
    for (let k = b - 1; k >= a; k--) if (s[k] === ']') { close = k; break; }
    if (close >= 0) for (let k = close; k < b; k++) skip.add(k);
  }
  const tokens: Token[] = [];
  const lowerWords = new Set<string>();
  for (const line of scalarLines(s)) {
    if (trimCFWhitespace(fromScalars(s, line[0], line[1])).startsWith(':::')) continue;
    for (const r of wordsIn(s, line)) {
      let skipped = false;
      for (let k = r[0]; k < r[1]; k++) if (skip.has(k)) { skipped = true; break; }
      if (skipped) continue;
      const word = fromScalars(s, r[0], r[1]);
      const parts: { range: [number, number]; word: string }[] = [];
      let start = r[0];
      for (let i = r[0]; i <= r[1]; i++) {
        if (!(i === r[1] || s[i] === '-')) continue;
        let end = i;
        // A possessive or contraction's ’s, ’d, ’ll stays out of the word.
        for (let k = end - 1; k >= start; k--) if (s[k] === '’' || s[k] === "'") { end = k; break; }
        if (end > start) parts.push({ range: [start, end], word: fromScalars(s, start, end) });
        start = i + 1;
      }
      tokens.push({ range: r, word, parts });
      if (charIsLowercase(firstChar(word))) lowerWords.add(word);
    }
  }
  return { s, tokens, lowerWords };
}

/** `startsSentence`: nothing before `i` in its paragraph but quotation marks, emphasis and spaces, or those after . ! ? */
export function startsSentence(s: readonly string[], i: number): boolean {
  let j = i - 1;
  while (j >= 0 && ' *_“‘"\'('.includes(s[j])) j--;
  return j < 0 || s[j] === '\n' || '.!?'.includes(s[j]);
}

/** `cased`: `word` in `like`'s case — all capitals, a capital first, or as given. */
function cased(word: string, like: string): string {
  if (graphemes(like).length > 1 && like === like.toUpperCase() && like !== like.toLowerCase()) return word.toUpperCase();
  if (charIsUppercase(firstChar(like))) return capitalFirst(word);
  return word;
}

// MARK: Spelling

const PAIRS: [string, string][] = [
  ['grey', 'gray'], ['greys', 'grays'], ['greyish', 'grayish'], ['greying', 'graying'], ['greyed', 'grayed'],
  ['towards', 'toward'], ['afterwards', 'afterward'], ['okay', 'ok'], ['judgement', 'judgment'], ['judgements', 'judgments'],
  ['acknowledgement', 'acknowledgment'], ['acknowledgements', 'acknowledgments'], ['aluminium', 'aluminum'],
  ['jewellery', 'jewelry'], ['pyjamas', 'pajamas'], ['plough', 'plow'], ['ploughed', 'plowed'], ['catalogue', 'catalog'],
  ['analogue', 'analog'], ['cosy', 'cozy'], ['sceptic', 'skeptic'], ['sceptical', 'skeptical'], ['scepticism', 'skepticism'],
  ['mould', 'mold'], ['mouldy', 'moldy'], ['smoulder', 'smolder'], ['smouldering', 'smoldering'], ['manoeuvre', 'maneuver'],
  ['manoeuvres', 'maneuvers'], ['encyclopaedia', 'encyclopedia'], ['foetus', 'fetus'], ['ageing', 'aging'],
  ['likeable', 'likable'], ['sizeable', 'sizable'], ['doughnut', 'donut'], ['doughnuts', 'donuts'], ['enrol', 'enroll'],
  ['fulfil', 'fulfill'], ['skilful', 'skillful'], ['wilful', 'willful'], ['instalment', 'installment'], ['defence', 'defense'],
  ['offence', 'offense'], ['pretence', 'pretense'], ['artefact', 'artifact'], ['artefacts', 'artifacts'], ['axe', 'ax'],
  ['moustache', 'mustache'], ['mum', 'mom'], ['mums', 'moms'], ['mummy', 'mommy'],
];
const PAIR_INDEX = new Map<string, { form: string; other: string }>();
for (const [a, b] of PAIRS) { PAIR_INDEX.set(a, { form: a, other: b }); PAIR_INDEX.set(b, { form: b, other: a }); }

const ISE_SUFFIXES = ['ations', 'ation', 'ational', 'ers', 'er', 'ing', 'ed', 'es', 'e', 'able'];
const OUR_SUFFIXES = new Set(['', 's', 'ed', 'ing', 'ful', 'fully', 'less', 'able', 'ably', 'ite', 'ites', 'er', 'ers', 'y', 'ism']);
const RE_SUFFIXES: [string, string][] = [['re', 'er'], ['res', 'ers'], ['red', 'ered'], ['ring', 'ering']];
const RE_EXCEPTIONS = new Set(['timb', 'met']);
const LL_SUFFIXES = ['ed', 'ing', 'er', 'ers', 'ous', 'ation', 'ations'];

const isVowel = (c: string) => 'aeiouy'.includes(c);
function syllables(word: string): number {
  let n = 0, inVowel = false;
  for (const c of graphemes(word)) { const v = isVowel(c); if (v && !inVowel) n++; inVowel = v; }
  return n;
}
const dropLast = (w: string, n: number) => graphemes(w).slice(0, Math.max(0, graphemes(w).length - n)).join('');
const glen = (w: string) => graphemes(w).length;

type Grouped = { group: string; occurrence: VariantOccurrence };

function spelling(part: { range: [number, number]; word: string }, sc: Scan, text: number): Grouped[] {
  const written = part.word;
  if (!(glen(written) >= 2 && allLetters(written))) return [];
  // A capital mid-sentence is a name's: not a spelling (OK aside).
  if (charIsUppercase(firstChar(written)) && written !== 'OK' && !startsSentence(sc.s, part.range[0])) return [];
  const lower = written.toLowerCase();
  const occurrence = (form: string, other: string): VariantOccurrence =>
    ({ text, range: part.range, written, form, alternative: cased(other, written) });
  const pair = PAIR_INDEX.get(lower);
  if (pair) {
    if (pair.form === 'ok' && written !== 'OK') return [];
    const form = pair.form === 'ok' ? 'OK' : pair.form, other = pair.other === 'ok' ? 'OK' : pair.other;
    const group = [pair.form, pair.other].sort().join('|');
    const alternative = other === 'OK' ? 'OK'
      : written === 'OK' ? cased(other, startsSentence(sc.s, part.range[0]) ? 'Ok' : 'ok') : cased(other, written);
    return [{ group, occurrence: { text, range: part.range, written, form, alternative } }];
  }
  for (const suffix of ISE_SUFFIXES) {
    for (const [here, there] of [['is', 'iz'], ['iz', 'is']]) {
      if (!lower.endsWith(here + suffix)) continue;
      const stem = dropLast(lower, here.length + suffix.length);
      if (glen(stem) < 3) continue;
      return [{ group: 'ise:' + stem, occurrence: occurrence(stem + here + 'e', stem + there + suffix) }];
    }
  }
  let r = lower.lastIndexOf('our');
  if (r >= 0) {
    const stem = lower.slice(0, r), suffix = lower.slice(r + 3);
    if (glen(stem) >= 3 && OUR_SUFFIXES.has(suffix)) return [{ group: 'our:' + stem, occurrence: occurrence(stem + 'our', stem + 'or' + suffix) }];
  }
  r = lower.lastIndexOf('or');
  if (r >= 0) {
    const stem = lower.slice(0, r), suffix = lower.slice(r + 2);
    if (glen(stem) >= 3 && OUR_SUFFIXES.has(suffix) && !stem.endsWith('u')) return [{ group: 'our:' + stem, occurrence: occurrence(stem + 'or', stem + 'our' + suffix) }];
  }
  for (const [re, er] of RE_SUFFIXES) {
    for (const [here, there, label] of [[re, er, 're'], [er, re, 'er']]) {
      if (!lower.endsWith(here)) continue;
      const stem = dropLast(lower, here.length);
      const last = stem.slice(-1);
      if (!(glen(stem) >= 3 && last !== '' && 'tbgv'.includes(last) && !RE_EXCEPTIONS.has(stem))) continue;
      return [{ group: 're:' + stem, occurrence: occurrence(stem + label, stem + there) }];
    }
  }
  for (const suffix of LL_SUFFIXES) {
    if (!lower.endsWith(suffix)) continue;
    const before = dropLast(lower, suffix.length);
    if (before.endsWith('ll')) {
      const stem = dropLast(before, 2);
      if (!(glen(stem) >= 4 && syllables(stem + 'l') >= 2 && isVowel(stem.slice(-1)))) continue;
      return [{ group: 'll:' + stem, occurrence: occurrence(stem + 'lled', stem + 'l' + suffix) }];
    } else if (before.endsWith('l')) {
      const stem = dropLast(before, 1);
      if (!(glen(stem) >= 4 && syllables(stem + 'l') >= 2 && isVowel(stem.slice(-1)))) continue;
      return [{ group: 'll:' + stem, occurrence: occurrence(stem + 'led', stem + 'll' + suffix) }];
    }
  }
  return [];
}

// MARK: Hyphenation, honorifics, capitals

function hyphenated(token: Token, sc: Scan, text: number): Grouped | null {
  const pieces = token.word.split('-');
  if (!(pieces.length === 2 && pieces.every((p) => glen(p) >= 1 && allLetters(p)) && glen(pieces[0]) + glen(pieces[1]) >= 4)) return null;
  if (charIsUppercase(firstChar(token.word)) && !startsSentence(sc.s, token.range[0])) return null;
  const lower = token.word.toLowerCase();
  return { group: lower, occurrence: { text, range: token.range, written: token.word, form: lower, alternative: token.word.replaceAll('-', '') } };
}

const HONORIFICS = new Set(['Mr', 'Mrs', 'Ms', 'Mx', 'Dr', 'Prof']);

function honorific(token: Token, sc: Scan, text: number): Grouped | null {
  if (!HONORIFICS.has(token.word)) return null;
  const s = sc.s;
  let end = token.range[1];
  const dotted = end < s.length && s[end] === '.';
  if (dotted) end++;
  if (!(end + 1 < s.length && (s[end] === ' ' || s[end] === ' ') && isUppercase(s[end + 1]))) return null;
  const form = token.word + (dotted ? '.' : '');
  return { group: token.word, occurrence: { text, range: [token.range[0], end], written: form, form, alternative: dotted ? token.word : token.word + '.' } };
}

const CAPITAL_WORDS = new Set(['mum', 'mom', 'dad', 'mummy', 'mommy', 'daddy', 'grandma', 'grandpa', 'nan', 'nana', 'gran', 'granny']);
const DETERMINERS = new Set([
  'my', 'your', 'his', 'her', 'our', 'their', 'its', 'a', 'an', 'the', 'whose', 'every', 'any', 'no',
  'single', 'new', 'old', 'real', 'step', 'birth', 'foster', 'own', 'proud', 'young', 'good', 'bad', 'dead', 'late',
  'future', 'first', 'adoptive', 'expectant', 'working', 'great', 'other', 'dear',
]);

function capital(token: Token, sc: Scan, text: number): Grouped | null {
  const lower = token.word.toLowerCase();
  if (!CAPITAL_WORDS.has(lower) || startsSentence(sc.s, token.range[0])) return null;
  const s = sc.s;
  const wordBefore = (i: number): { word: string; start: number } | null => {
    let j = i - 1;
    while (j >= 0 && (s[j] === ' ' || s[j] === '*' || s[j] === '_')) j--;
    if (!(j >= 0 && (isAlphabetic(s[j]) || s[j] === '’' || s[j] === "'"))) return null;
    let k = j;
    while (k > 0 && (isAlphabetic(s[k - 1]) || s[k - 1] === '’' || s[k - 1] === "'")) k--;
    return { word: fromScalars(s, k, j + 1).toLowerCase(), start: k };
  };
  let before = wordBefore(token.range[0]);
  if (before && (before.word === 'and' || before.word === 'or')) {
    const family = wordBefore(before.start);
    if (family && CAPITAL_WORDS.has(family.word)) before = wordBefore(family.start);
  }
  const common = before !== null && (DETERMINERS.has(before.word) || before.word.endsWith('’s') || before.word.endsWith("'s")
    || before.word.endsWith('s’') || before.word.endsWith("s'"));
  const capitalised = charIsUppercase(firstChar(token.word));
  const form = capitalised ? capitalFirst(lower) : lower;
  const alternative = capitalised === common ? (common ? lower : capitalFirst(lower)) : null;
  return { group: lower, occurrence: { text, range: token.range, written: token.word, form, alternative } };
}

// MARK: Names

/** `oneEditApart`: a letter added or dropped, or two side by side swapped. */
export function oneEditApart(a: string[], b: string[]): boolean {
  if (a.length === b.length && a.every((c, i) => c === b[i])) return false;
  if (a.length === b.length) {
    const diff = a.map((c, i) => (c !== b[i] ? i : -1)).filter((i) => i >= 0);
    return diff.length === 2 && diff[1] === diff[0] + 1 && a[diff[0]] === b[diff[1]] && a[diff[1]] === b[diff[0]];
  }
  const [short, long] = a.length < b.length ? [a, b] : [b, a];
  if (long.length - short.length !== 1) return false;
  let i = 0;
  while (i < short.length && short[i] === long[i]) i++;
  return short.slice(i).every((c, k) => c === long[i + 1 + k]);
}

function names(scanned: Scan[], o: Required<VariantOptions>): VariantSet[] {
  const nameWords = new Set(o.names.flatMap((n) => n.split(/[^\p{L}]+/u).filter((w) => glen(w) >= 4 && charIsUppercase(firstChar(w)))));
  if (nameWords.size === 0) return [];
  const counts = new Map<string, number>();
  const places = new Map<string, VariantOccurrence[]>();
  const lower = new Set<string>();
  scanned.forEach((sc, t) => {
    for (const w of sc.lowerWords) lower.add(w);
    const capitalisedNeighbour = (k: number) => k >= 0 && k < sc.tokens.length && charIsUppercase(firstChar(sc.tokens[k].word));
    sc.tokens.forEach((token, k) => {
      const joined = (other: number) => {
        if (!capitalisedNeighbour(other)) return false;
        const [a, b] = other < k ? [sc.tokens[other].range[1], token.range[0]] : [token.range[1], sc.tokens[other].range[0]];
        return sc.s.slice(a, b).every((c) => c === ' ' || c === '&');
      };
      const inFullName = (joined(k - 1) && !startsSentence(sc.s, sc.tokens[k - 1].range[0])) || joined(k + 1);
      for (const part of token.parts) {
        const w = part.word;
        if (glen(w) < 4) continue;
        const g = graphemes(w);
        if (!(charIsUppercase(g[0]) && g.slice(1).every(charIsLowercase))) continue;
        counts.set(w, (counts.get(w) ?? 0) + 1);
        if (!nameWords.has(w) && !inFullName) {
          const list = places.get(w) ?? [];
          list.push({ text: t, range: part.range, written: w, form: w, alternative: null });
          places.set(w, list);
        }
      }
    });
  });
  const sets: VariantSet[] = [];
  const sortedNames = [...nameWords].sort();
  for (const [word, occurrences] of places) {
    if (occurrences.length > 3 || lower.has(word.toLowerCase())) continue;
    const letters = graphemes(word);
    const name = sortedNames.find((n) => (counts.get(n) ?? 0) >= 5 * occurrences.length && oneEditApart(letters, graphemes(n)));
    if (!name) continue;
    const forms = [name, word];
    const key = variantKey('names', forms);
    const choice = o.choices.find((c) => choiceKey(c) === key);
    if (choice && choice.keep === null) continue;
    sets.push({
      id: stableUUID('style-choice:' + key), kind: 'names', key, forms, counts: { [name]: counts.get(name) ?? 0, [word]: occurrences.length },
      occurrences: occurrences.map((x) => ({ ...x, alternative: name })), kept: choice?.keep === word ? word : name,
    });
  }
  return sets;
}

// MARK: Numbers

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen',
  'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

/** `spelledOut`: 0…100 in words, as English writes them. */
export function spelledOut(n: number): string {
  if (n < 20) return NUMBER_WORDS[n];
  if (n === 100) return 'one hundred';
  return TENS[Math.floor(n / 10)] + (n % 10 === 0 ? '' : '-' + NUMBER_WORDS[n % 10]);
}

const NUMBER_LABELS = new Set([
  'no', 'number', 'page', 'pages', 'p', 'pp', 'chapter', 'room', 'flat', 'apartment', 'platform', 'gate', 'level', 'track',
  'verse', 'line', 'route', 'highway', 'bus', 'row', 'seat', 'size', 'channel', 'station', 'exit', 'unit', 'suite', 'box',
  'volume', 'vol', 'part', 'act', 'scene', 'aged', 'age', 'minus', 'plus', 'version', 'floor', 'lane', 'street', 'road',
]);
const NUMBER_UNITS = new Set([
  'am', 'pm', 'a', 'p', 'o’clock', "o'clock", 'km', 'kg', 'cm', 'mm', 'm', 'g', 'mg', 'ml', 'l', 'mph', 'kph', 'min', 'mins',
  'h', 'hr', 'hrs', 'percent', 'per', 'degrees', 'x', 'lb', 'lbs', 'oz', 'ft', 'in', 'bpm', 'k',
]);

function numbers(scanned: Scan[], o: Required<VariantOptions>): VariantSet | null {
  if (!(o.language ?? 'en').toLowerCase().startsWith('en') || o.numbers === 'digits') return null;
  let spelled = 0;
  const digits: VariantOccurrence[] = [];
  scanned.forEach((sc, t) => {
    const s = sc.s;
    const listStarts = new Set(listMarkers(s).keys());
    const tokens = sc.tokens;
    tokens.forEach((token, k) => {
      const lower = token.word.toLowerCase();
      if (toScalars(token.word).every(isDigit)) {
        const n = Number(token.word);
        if (!(graphemes(token.word).length <= 3 && n <= 100)) return;
        const [lo, hi] = token.range;
        if (listStarts.has(lo)) return;
        if (lo > 0 && '^.,:/-–—$£€#\'’+\\×('.includes(s[lo - 1])) return;
        if (hi < s.length) {
          const after = s[hi];
          if ('%°′″/×-–—”’"\''.includes(after)) return;
          if ('.,:-–—'.includes(after) && hi + 1 < s.length && isDigit(s[hi + 1])) return;
        }
        if (k > 0) {
          const previous = tokens[k - 1];
          const gap = s.slice(previous.range[1], lo);
          if (gap.every((c) => c === ' ') && charIsUppercase(firstChar(previous.word))) return;
          // A group of a number written with spaces (`60 000`).
          if (gap.every((c) => c === ' ') && toScalars(previous.word).every(isDigit)) return;
          if (gap.every((c) => c === ' ' || c === '.') && NUMBER_LABELS.has(previous.word.toLowerCase())) return;
        }
        if (k + 1 < tokens.length) {
          const next = tokens[k + 1];
          const gap = s.slice(hi, next.range[0]);
          if (gap.every((c) => c === ' ') && NUMBER_UNITS.has(next.word.toLowerCase())) return;
          // Before a name (`3 Triple R`) or another group (`60 000`).
          if (gap.every((c) => c === ' ') && (charIsUppercase(firstChar(next.word)) || toScalars(next.word).every(isDigit))) return;
        }
        digits.push({ text: t, range: token.range, written: token.word, form: 'digits', alternative: spelledOut(n) });
      } else if (lower !== 'one' && lower !== 'zero' && (NUMBER_WORDS.includes(lower) || TENS.includes(lower)
        || (lower.split('-').length === 2 && TENS.includes(lower.split('-')[0])) || lower === 'hundred')) {
        spelled++;
      }
    });
  });
  if (digits.length === 0 || !(o.numbers === 'words' || spelled > digits.length)) return null;
  const forms = ['words', 'digits'];
  const key = variantKey('numbers', forms);
  return { id: stableUUID('style-choice:' + key), kind: 'numbers', key, forms, counts: { words: spelled, digits: digits.length }, occurrences: digits, kept: 'words' };
}

// MARK: Finding

function make(kind: VariantKind, occurrences: VariantOccurrence[], o: Required<VariantOptions>): VariantSet | null {
  const counts: Record<string, number> = {};
  const first = new Map<string, [number, number]>();
  for (const x of occurrences) {
    counts[x.form] = (counts[x.form] ?? 0) + 1;
    if (!first.has(x.form)) first.set(x.form, [x.text, x.range[0]]);
  }
  const before = (a: [number, number], b: [number, number]) => a[0] < b[0] || (a[0] === b[0] && a[1] < b[1]);
  const present = Object.keys(counts).sort((a, b) => (counts[a] !== counts[b] ? counts[b] - counts[a] : before(first.get(a)!, first.get(b)!) ? -1 : 1));
  const forms = [...present];
  if (forms.length === 1 && occurrences[0]?.alternative != null) {
    const alt = occurrences[0].alternative;
    forms.push(kind === 'honorifics' || kind === 'names' ? alt : alt.toLowerCase());
  }
  if (forms.length !== 2) return null;
  const key = variantKey(kind, forms);
  const choice = o.choices.find((c) => choiceKey(c) === key);
  if (choice && choice.keep === null) return null;
  if (!(present.length === 2 || choice)) return null;
  const kept = choice?.keep ? forms.find((f) => f.toLowerCase() === choice.keep!.toLowerCase()) ?? null : null;
  return { id: stableUUID('style-choice:' + key), kind, key, forms, counts, occurrences, kept };
}

/** `TextVariants.find`: every set of variants in these texts that has something to flag, in reading order. */
export function findVariants(texts: readonly string[], options: VariantOptions = {}): VariantSet[] {
  const o: Required<VariantOptions> = { names: options.names ?? [], choices: options.choices ?? [], numbers: options.numbers ?? null, language: options.language ?? null };
  const scanned = texts.map((t) => scan(toScalars(t)));
  const raw = new Map<VariantKind, Map<string, VariantOccurrence[]>>();
  const add = (kind: VariantKind, group: string, x: VariantOccurrence) => {
    const byGroup = raw.get(kind) ?? new Map<string, VariantOccurrence[]>();
    raw.set(kind, byGroup);
    const list = byGroup.get(group) ?? [];
    list.push(x);
    byGroup.set(group, list);
  };
  scanned.forEach((sc, t) => {
    for (const token of sc.tokens) {
      const h = hyphenated(token, sc, t);
      if (h) add('hyphenation', h.group, h.occurrence);
      for (const part of token.parts) for (const g of spelling(part, sc, t)) add('spelling-variants', g.group, g.occurrence);
      const hon = honorific(token, sc, t);
      if (hon) add('honorifics', hon.group, hon.occurrence);
      const cap = capital(token, sc, t);
      if (cap) add('family-words', cap.group, cap.occurrence);
    }
  });
  const hyphenGroups = raw.get('hyphenation');
  if (hyphenGroups) {
    const closed = new Map<string, string[]>();
    for (const g of hyphenGroups.keys()) {
      const k = g.replaceAll('-', '');
      closed.set(k, [...(closed.get(k) ?? []), g]);
    }
    scanned.forEach((sc, t) => {
      for (const token of sc.tokens) {
        if (token.word.includes('-')) continue;
        if (charIsUppercase(firstChar(token.word)) && !startsSentence(sc.s, token.range[0])) continue;
        const lower = token.word.toLowerCase();
        const candidates = closed.get(lower);
        if (!candidates) continue;
        const hyphenatedForm = [...candidates].sort()[0];
        const hyphen = graphemes(hyphenatedForm).indexOf('-');
        const g = graphemes(token.word);
        const alternative = [...g.slice(0, hyphen), '-', ...g.slice(hyphen)].join('');
        add('hyphenation', hyphenatedForm, { text: t, range: token.range, written: token.word, form: lower, alternative });
      }
    });
  }
  const sets: VariantSet[] = [];
  for (const occurrences of raw.get('family-words')?.values() ?? []) {
    const counts: Record<string, number> = {};
    for (const x of occurrences) counts[x.form] = (counts[x.form] ?? 0) + 1;
    const lower = occurrences[0].form.toLowerCase();
    const forms = [capitalFirst(lower), lower];
    const key = variantKey('family-words', forms);
    sets.push({ id: stableUUID('style-choice:' + key), kind: 'family-words', key, forms, counts, occurrences, kept: null });
  }
  for (const kind of ['spelling-variants', 'hyphenation', 'honorifics'] as VariantKind[]) {
    for (const occurrences of raw.get(kind)?.values() ?? []) {
      const set = make(kind, occurrences, o);
      if (set) sets.push(set);
    }
  }
  sets.push(...names(scanned, o));
  const n = numbers(scanned, o);
  if (n) sets.push(n);
  return sets.filter((x) => flaggedOccurrences(x).length > 0).sort((a, b) => {
    const x = flaggedOccurrences(a)[0], y = flaggedOccurrences(b)[0];
    return x.text - y.text || x.range[0] - y.range[0] || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
  });
}

// MARK: The style sheet's choices

export const CHOICE_PREFIX = 'styleSheet.choice.';

/** `ProjectDocument.styleChoices()`: one JSON entry per choice at `settings["styleSheet.choice.<id>"]`, by key order. */
export function readStyleChoices(settings: Record<string, unknown>): VariantChoice[] {
  return Object.keys(settings).filter((k) => k.startsWith(CHOICE_PREFIX)).sort().flatMap((k) => {
    const raw = settings[k];
    if (typeof raw !== 'string') return [];
    try {
      const v = JSON.parse(raw) as { kind?: unknown; forms?: unknown; keep?: unknown };
      if (!VARIANT_KINDS.includes(v.kind as VariantKind) || !Array.isArray(v.forms) || !v.forms.every((f) => typeof f === 'string')) return [];
      return [{ kind: v.kind as VariantKind, forms: v.forms as string[], keep: typeof v.keep === 'string' ? v.keep : null }];
    } catch {
      return [];
    }
  });
}
